import { useCallback, useEffect, useState } from 'react';
import { ApiError, errorMessage } from '../api/client';
import {
  posApi,
  printAddressLabel,
  printFromAddressLabel,
  type OnlineOrder,
  type OnlineOrders,
} from '../api/pos';
import { billDateTime } from '../format';

const PAGE_SIZE = 25;

/** Stands in for an order id while the shop's own slip is printing. */
const FROM_SLIP = 'from';

const STATUS_LABELS: Record<string, string> = {
  PLACED: 'Placed',
  CONFIRMED: 'Confirmed',
  SHIPPED: 'Shipped',
  OUT_FOR_DELIVERY: 'Out for delivery',
  DELIVERED: 'Delivered',
  FAILED_DELIVERY: 'Delivery failed',
  CANCELLED: 'Cancelled',
};

const DELIVERY_LABELS: Record<string, string> = {
  POSTAL: 'Courier',
  SAME_DAY: 'Same day',
  TWO_HOUR: '2-hour',
};

/**
 * The website's orders, for whoever at the counter is packing them.
 *
 * Each row answers the two questions a parcel raises — what goes in it and
 * where it is going — and prints the second on its own, as the slip for the
 * outside of the box. Read-only: nothing here changes an order, and no price
 * or payment is shown, because the counter has no use for either.
 *
 * Titles are listed in full rather than cut short the way a bill row's are.
 * A bill row is for recognising a sale; this one is what the books are picked
 * off the shelf from.
 */
export default function OnlineOrdersScreen() {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);

  const [data, setData] = useState<OnlineOrders | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // The one order whose slip is on its way to the printer, and the one whose
  // slip failed — per row, so a refusal is read next to the button it came from.
  const [printing, setPrinting] = useState<string | null>(null);
  const [printError, setPrintError] = useState<{ id: string; message: string } | null>(null);

  useEffect(() => {
    const t = window.setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 350);
    return () => window.clearTimeout(t);
  }, [search]);

  const load = useCallback(
    (signal?: AbortSignal) => {
      setLoading(true);
      posApi
        // Only what is waiting to be packed: an order that has shipped, been
        // delivered or been cancelled is no longer the counter's business.
        .onlineOrders({ status: 'open', search: debouncedSearch, page, limit: PAGE_SIZE }, signal)
        .then((res) => {
          setData(res);
          setError(null);
        })
        .catch((err: unknown) => {
          if (signal?.aborted) return;
          // A server older than this screen has no such route.
          setError(
            err instanceof ApiError && err.status === 404
              ? 'Online orders are not available on this server yet.'
              : errorMessage(err, 'Could not load the online orders.'),
          );
        })
        .finally(() => {
          if (!signal?.aborted) setLoading(false);
        });
    },
    [debouncedSearch, page],
  );

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load]);

  async function printAddress(order: OnlineOrder) {
    setPrinting(order.id);
    setPrintError(null);
    try {
      await printAddressLabel(order.id);
    } catch (err) {
      setPrintError({
        id: order.id,
        message: errorMessage(err, 'Could not print the address.'),
      });
    } finally {
      setPrinting(null);
    }
  }

  // The shop's own slip is keyed apart from the orders', so its button and its
  // refusal never borrow a row's.
  async function printFromAddress() {
    setPrinting(FROM_SLIP);
    setPrintError(null);
    try {
      await printFromAddressLabel();
    } catch (err) {
      setPrintError({
        id: FROM_SLIP,
        message: errorMessage(err, 'Could not print the from address.'),
      });
    } finally {
      setPrinting(null);
    }
  }

  const fromAddress = data?.fromAddress;
  const orders = data?.orders ?? [];
  const totalPages = data?.totalPages ?? 1;

  return (
    <div className="online">
      <div className="filters">
        <div className="filter-top">
          <div className="filter-presets">
            <span className="muted">Confirmed orders waiting to be packed</span>
          </div>
          <div className="filter-actions">
            <button type="button" className="btn" onClick={() => load()}>
              Refresh
            </button>
          </div>
        </div>

        <div className="filter-row">
          <label className="field field-grow">
            <span>Order number</span>
            <input
              type="search"
              value={search}
              placeholder="e.g. BLO-1234"
              onChange={(e) => setSearch(e.target.value)}
            />
          </label>
        </div>
      </div>

      {/* Once, above the orders: every parcel leaves from the same shop, so the
          sender is not repeated on each card. */}
      {fromAddress && fromAddress.lines.length > 0 && (
        <div className="online-from">
          <div className="online-address">
            <span className="cashup-label">From</span>
            <strong>{fromAddress.recipientName}</strong>
            {fromAddress.lines.map((line, i) => (
              <span key={i}>{line}</span>
            ))}
            {fromAddress.phone && <span>Ph: {fromAddress.phone}</span>}
          </div>
          <div className="online-from-actions">
            <button
              type="button"
              className="btn"
              disabled={printing === FROM_SLIP}
              onClick={printFromAddress}
            >
              {printing === FROM_SLIP ? 'Printing…' : 'Print from address'}
            </button>
            {printError?.id === FROM_SLIP && (
              <p className="error">{printError.message}</p>
            )}
          </div>
        </div>
      )}

      {error && (
        <div>
          <p className="error">{error}</p>
          <button type="button" className="btn" onClick={() => load()}>
            Try again
          </button>
        </div>
      )}

      {loading ? (
        <p className="muted">Loading online orders…</p>
      ) : error ? null : orders.length === 0 ? (
        <p className="muted empty">
          {debouncedSearch
            ? `No confirmed online order matches “${debouncedSearch}”.`
            : 'No online orders waiting to be packed.'}
        </p>
      ) : (
        <>
          <ul className="online-list">
            {orders.map((order) => {
              const address = order.shippingAddress;
              const cancelled = order.status === 'CANCELLED';
              return (
                <li key={order.id} className="online-row">
                  <div className="online-head">
                    <span className="bill-no">
                      {order.orderNumber}
                      <span className={cancelled ? 'void-tag' : 'online-status'}>
                        {STATUS_LABELS[order.status] ?? order.status}
                      </span>
                    </span>
                    <span className="bill-time">
                      {billDateTime(order.createdAt)}
                      {' · '}
                      {DELIVERY_LABELS[order.deliveryType] ?? order.deliveryType}
                    </span>
                  </div>

                  <div className="online-body">
                    <ul className="online-titles">
                      {order.items.map((item) => (
                        <li key={item.id}>
                          <span className="online-qty">{item.quantity} ×</span>
                          <span>{item.title}</span>
                        </li>
                      ))}
                    </ul>

                    <div className="online-address">
                      <span className="cashup-label">Ship to</span>
                      <strong>{address.recipientName}</strong>
                      {address.lines.length === 0 ? (
                        <span className="muted">No shipping address on this order.</span>
                      ) : (
                        address.lines.map((line, i) => <span key={i}>{line}</span>)
                      )}
                      {address.phone && <span>Ph: {address.phone}</span>}

                      <button
                        type="button"
                        className="btn"
                        disabled={printing === order.id || address.lines.length === 0}
                        onClick={() => printAddress(order)}
                      >
                        {printing === order.id ? 'Printing…' : 'Print address'}
                      </button>
                      {printError?.id === order.id && (
                        <p className="error">{printError.message}</p>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>

          {totalPages > 1 && (
            <div className="pager">
              <button
                type="button"
                className="btn"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Previous
              </button>
              <span className="muted">
                Page {page} of {totalPages} · {data?.total ?? 0} orders
              </span>
              <button
                type="button"
                className="btn"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
